import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, MapPin, UserCog, Loader2, Building2, X, Inbox, Check, Ban, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";

const REV_MODELS = [
  { v: "eyw_hosted", label: "EYW hosted (70/30)" },
  { v: "enhancer_location", label: "Enhancer / affiliate (90/10)" },
  { v: "partner", label: "Partner (80/10/10)" },
  { v: "online", label: "Online (80/20)" },
];

export default function LocationsPanel() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-locations"], queryFn: () => api.get("/admin/locations").then((r) => r.data) });
  const [modal, setModal] = useState(null);
  const [mgrModal, setMgrModal] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-locations"] });
  const del = async (l) => {
    if (!window.confirm(`Delete "${l.name}"? Rooms will be detached.`)) return;
    try { await api.delete(`/admin/locations/${l.id}`); toast.success("Location deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="admin-locations-panel">
      <PartnerApplications onApproved={refresh} />
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">Center Locations</h2>
          <p className="text-sm text-[#4A5D6B]">Flagship + Network Partner centers. Partners are a membership upgrade — invite &amp; assign a Location Manager, then publish to feature it in the public Network Partner spotlight.</p>
        </div>
        <span className="text-xs text-[#8FA89B] max-w-[280px] text-right" data-testid="admin-no-create-location">New Centers join through the Partner application flow and stay owned by that partner. Admins review, edit, approve, pause &amp; moderate.</span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(list.data || []).map((l) => (
          <div key={l.id} className="eyw-card p-6" data-testid={`admin-location-${l.id}`}>
            <div className="flex justify-between items-start">
              <h3 className="text-lg text-[#1A242B] flex items-center gap-2"><MapPin size={16} className="text-[#D98E73]" /> {l.name}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${l.kind === "flagship" ? "bg-[#144F75]/10 text-[#144F75]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{l.kind === "flagship" ? "Flagship" : "Network Partner"}</span>
            </div>
            <p className="text-xs text-[#4A5D6B] mt-2">{l.city}{l.state ? `, ${l.state}` : ""} · {l.room_count} rooms · {l.enhancer_count} enhancers</p>
            <p className="text-xs text-[#8FA89B] mt-1">{l.revenue_model} · {l.enhancer_pct}/{l.ecosystem_pct}{l.kind !== "flagship" && ` · ${l.membership_status}`}</p>
            <p className="text-xs mt-2 flex items-center gap-1.5 text-[#4A5D6B]"><UserCog size={13} /> {l.manager_name || <span className="text-[#B5794E]">No manager</span>}</p>
            <span className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full ${l.published !== false ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#F1EEE8] text-[#8FA89B]"}`} data-testid={`admin-loc-pubstate-${l.id}`}>{l.published !== false ? "Published — live on site" : "Draft — not published"}</span>
            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={() => setModal(l)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
              <button onClick={() => setMgrModal(l)} data-testid={`admin-loc-manager-${l.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><UserCog size={13} /> Manager</button>
              {l.kind !== "flagship" && <button onClick={() => del(l)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>}
            </div>
          </div>
        ))}
        {list.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No locations yet.</p>}
      </div>
      {modal && <LocationModal location={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={refresh} />}
      {mgrModal && <ManagerModal location={mgrModal} onClose={() => setMgrModal(null)} onSaved={refresh} />}
    </div>
  );
}

function PartnerApplications({ onApproved }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const [statusF, setStatusF] = useState("pending");
  const [q, setQ] = useState("");
  const query = useQuery({ queryKey: ["partner-apps"], queryFn: () => api.get("/admin/partner-applications").then((r) => r.data) });
  const all = query.data || [];
  const counts = {
    all: all.length,
    pending: all.filter((a) => a.status === "pending").length,
    approved: all.filter((a) => a.status === "approved").length,
    rejected: all.filter((a) => a.status === "rejected").length,
  };
  const ql = q.trim().toLowerCase();
  const apps = all.filter((a) => {
    if (statusF !== "all" && a.status !== statusF) return false;
    if (!ql) return true;
    return [a.business_name, a.contact_name, a.email, a.city, a.state].filter(Boolean).join(" ").toLowerCase().includes(ql);
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["partner-apps"] }); qc.invalidateQueries({ queryKey: ["admin-partner-apps-count"] }); };
  const approve = async (a) => {
    if (!window.confirm(`Approve "${a.business_name}"? This creates & publishes a Network Partner center and invites ${a.email} as its manager.`)) return;
    setBusy(a.id + "ok");
    try {
      const r = await api.post(`/admin/partner-applications/${a.id}/approve`, { origin: window.location.origin });
      toast.success(r.data?.invited ? "Approved, published & manager invited" : "Approved & published");
      refresh(); onApproved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const reject = async (a) => {
    if (!window.confirm(`Reject "${a.business_name}"?`)) return;
    setBusy(a.id + "no");
    try { await api.post(`/admin/partner-applications/${a.id}/reject`); toast.success("Application rejected"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  if (!query.isLoading && counts.all === 0) return null;
  const FILTERS = [
    { k: "pending", label: "Pending" }, { k: "approved", label: "Approved" },
    { k: "rejected", label: "Rejected" }, { k: "all", label: "All" },
  ];
  const STATUS_PILL = {
    pending: "bg-[#D98E73]/15 text-[#B5794E]", approved: "bg-[#5C8A72]/15 text-[#5C8A72]", rejected: "bg-red-100 text-red-600",
  };
  return (
    <div className="mb-8 rounded-2xl border border-[#9E5C7F]/30 bg-[#9E5C7F]/[0.04] p-5" data-testid="partner-applications">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><Inbox size={18} className="text-[#9E5C7F]" /> Partner applications
          {counts.pending > 0 && <span className="text-xs font-sans px-2 py-0.5 rounded-full bg-[#9E5C7F] text-white">{counts.pending} pending</span>}
        </h3>
        <div className="flex items-center gap-2 eyw-input !py-1.5 max-w-xs w-full">
          <Search size={15} className="text-[#8FA89B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, city" data-testid="partner-app-search" className="bg-transparent outline-none flex-1 text-sm" />
        </div>
      </div>
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {FILTERS.map((fl) => (
          <button key={fl.k} onClick={() => setStatusF(fl.k)} data-testid={`partner-app-filter-${fl.k}`}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${statusF === fl.k ? "bg-[#9E5C7F] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#9E5C7F]"}`}>
            {fl.label} <span className="opacity-70">({counts[fl.k]})</span>
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {apps.length === 0 && <p className="text-sm text-[#8FA89B]" data-testid="partner-app-empty">No {statusF !== "all" ? statusF : ""} applications{ql ? " match your search" : ""}.</p>}
        {apps.map((a) => (
          <div key={a.id} className="bg-white rounded-xl border border-[#E5E0D8] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`partner-app-${a.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-[#1A242B] flex items-center gap-2">{a.business_name}
                <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${STATUS_PILL[a.status] || "bg-[#F1EEE8] text-[#8FA89B]"}`}>{a.status}</span>
                <span className="text-xs font-normal text-[#8FA89B]">{[a.city, a.state].filter(Boolean).join(", ")}</span>
              </p>
              <p className="text-xs text-[#4A5D6B] mt-0.5">{a.contact_name ? `${a.contact_name} · ` : ""}{a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
              {a.description && <p className="text-xs text-[#4A5D6B] mt-1 line-clamp-2">{a.description}</p>}
              <p className="text-[11px] text-[#8FA89B] mt-1">Applied {String(a.created_at || "").slice(0, 10)}{a.approved_at ? ` · approved ${String(a.approved_at).slice(0, 10)}` : ""}{a.rejected_at ? ` · rejected ${String(a.rejected_at).slice(0, 10)}` : ""}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              {a.status === "pending" ? (
                <>
                  <button onClick={() => approve(a)} disabled={!!busy} data-testid={`partner-app-approve-${a.id}`} className="eyw-btn !py-1.5 !px-3 text-xs !bg-[#5C8A72] hover:!bg-[#4a7360]">{busy === a.id + "ok" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve & publish</button>
                  <button onClick={() => reject(a)} disabled={!!busy} data-testid={`partner-app-reject-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500">{busy === a.id + "no" ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Reject</button>
                </>
              ) : a.status === "approved" && a.center_slug ? (
                <a href={`/locations/${a.center_slug}`} target="_blank" rel="noreferrer" data-testid={`partner-app-view-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs">View center</a>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LocationModal({ location, onClose, onSaved }) {
  const [f, setF] = useState({
    name: location?.name || "", kind: location?.kind || "affiliate", description: location?.description || "",
    image: location?.image || "", address: location?.address || "", city: location?.city || "",
    state: location?.state || "", zip: location?.zip || "", phone: location?.phone || "", email: location?.email || "",
    revenue_model: location?.revenue_model || "eyw_hosted", enhancer_pct: location?.enhancer_pct ?? 70,
    ecosystem_pct: location?.ecosystem_pct ?? 30, membership_status: location?.membership_status || "active",
    published: location?.published !== false, active: location?.active !== false,
    hours: location?.hours || [],
    website: location?.website || "", timezone: location?.timezone || "",
    social_links: location?.social_links || {},
    hourly_rate: location?.hourly_rate ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Please add a name to continue.");
    setSaving(true);
    try {
      if (location) await api.put(`/admin/locations/${location.id}`, f);
      else await api.post("/admin/locations", f);
      toast.success("Location saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const inp = (label, key, ph = "") => (
    <div><label className="text-sm font-medium mb-1 block">{label}</label>
      <input value={f[key]} onChange={(e) => set(key, e.target.value)} placeholder={ph} data-testid={`admin-loc-${key}`} className="eyw-input w-full" /></div>
  );
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="admin-location-modal">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Building2 size={18} /> {location ? "Edit location" : "New location"}</h3>
          <button onClick={onClose}><X size={18} className="text-[#4A5D6B]" /></button>
        </div>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {inp("Center name", "name")}
            <div><label className="text-sm font-medium mb-1 block">Kind</label>
              <select value={f.kind} onChange={(e) => set("kind", e.target.value)} data-testid="admin-loc-kind" className="eyw-input w-full">
                <option value="affiliate">Network Partner</option><option value="flagship">Flagship</option>
              </select></div>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Description</label>
            <RichTextEditor value={f.description} onChange={(v) => set("description", v)} testid="admin-loc-description" placeholder="Describe this center — add headings, images and video…" minHeight={140} /></div>
          <ImageUpload value={f.image} onChange={(url) => set("image", url)} label="Cover image" testid="admin-loc-image" />
          <div className="grid md:grid-cols-3 gap-4">
            {inp("Address", "address")}{inp("City", "city")}{inp("State", "state")}
            {inp("Zip", "zip")}{inp("Phone", "phone")}{inp("Email", "email")}
          </div>
          <div><label className="text-sm font-medium mb-1 block">Default hourly pay rate ($/hr)</label>
            <input type="number" min="0" step="0.5" value={f.hourly_rate} onChange={(e) => set("hourly_rate", parseFloat(e.target.value) || 0)} data-testid="admin-loc-hourly_rate" className="eyw-input w-full" />
            <p className="text-xs text-[#8FA89B] mt-1">Used for payroll when an enhancer has no personal rate set.</p>
            {inp("Website", "website")}{inp("Timezone (e.g. America/New_York)", "timezone")}
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Social & media links</label>
            <div className="grid grid-cols-2 gap-2" data-testid="admin-loc-social">
              {["instagram", "facebook", "youtube", "spotify", "tiktok", "podcast"].map((k) => (
                <input key={k} value={f.social_links?.[k] || ""} onChange={(e) => set("social_links", { ...f.social_links, [k]: e.target.value })}
                  placeholder={`${k[0].toUpperCase() + k.slice(1)} URL`} data-testid={`admin-loc-social-${k}`} className="eyw-input w-full text-sm" />
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-[#F9F7F3] p-4 space-y-3">
            <p className="text-sm font-medium text-[#1A242B]">Legacy revenue model (reference only)</p>
            <p className="text-xs text-[#B5794E]" data-testid="admin-loc-revenue-note">New sales follow the ecosystem rule: 10% ecosystem, the Center's agreed % or fixed amount (set per offering in Revenue Split), remainder to the enhancer. These legacy fields no longer change new transactions.</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div><label className="text-xs text-[#4A5D6B] mb-1 block">Model</label>
                <select value={f.revenue_model} onChange={(e) => set("revenue_model", e.target.value)} data-testid="admin-loc-revenue" className="eyw-input w-full text-sm">
                  {REV_MODELS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select></div>
              <div><label className="text-xs text-[#4A5D6B] mb-1 block">Enhancer %</label>
                <input type="number" value={f.enhancer_pct} onChange={(e) => set("enhancer_pct", parseInt(e.target.value) || 0)} className="eyw-input w-full text-sm" /></div>
              <div><label className="text-xs text-[#4A5D6B] mb-1 block">Ecosystem %</label>
                <input type="number" value={f.ecosystem_pct} onChange={(e) => set("ecosystem_pct", parseInt(e.target.value) || 0)} className="eyw-input w-full text-sm" /></div>
            </div>
            {f.kind === "affiliate" && (
              <div><label className="text-xs text-[#4A5D6B] mb-1 block">Membership status (affiliate upgrade)</label>
                <select value={f.membership_status} onChange={(e) => set("membership_status", e.target.value)} data-testid="admin-loc-membership" className="eyw-input w-full text-sm">
                  <option value="active">Active</option><option value="pending">Pending</option><option value="inactive">Inactive</option>
                </select></div>
            )}
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={f.published} onChange={(e) => set("published", e.target.checked)} /> Published</label>
            <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="admin-loc-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Save</button>
        </div>
      </div>
    </div>
  );
}

function ManagerModal({ location, onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [inviteNew, setInviteNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const assign = async () => {
    if (!email.trim()) return;
    setSaving(true);
    try {
      const body = { email: email.trim() };
      if (inviteNew) { body.invite = true; body.name = name.trim(); body.origin = window.location.origin; }
      const r = await api.post(`/admin/locations/${location.id}/manager`, body);
      toast.success(r.data?.invited ? "Manager invited & assigned — set-password email sent" : "Manager assigned");
      onSaved(); onClose();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const unassign = async () => {
    try { await api.delete(`/admin/locations/${location.id}/manager`); toast.success("Manager removed"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="admin-manager-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-1 flex items-center gap-2"><UserCog size={18} /> Location manager</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">{location.name}</p>
        {location.manager_name && <p className="text-sm mb-3 rounded-lg bg-[#F9F7F3] px-3 py-2">Current: <b>{location.manager_name}</b></p>}
        <label className="flex items-center gap-2 text-sm text-[#4A5D6B] mb-3 rounded-lg bg-[#144F75]/[0.04] px-3 py-2">
          <input type="checkbox" checked={inviteNew} onChange={(e) => setInviteNew(e.target.checked)} data-testid="admin-manager-invite-toggle" />
          Invite a new manager (they don't have an account yet)
        </label>
        {inviteNew && (
          <div className="mb-3">
            <label className="text-sm font-medium mb-1 block">Manager name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="admin-manager-name" className="eyw-input w-full" />
          </div>
        )}
        <label className="text-sm font-medium mb-1 block">{inviteNew ? "Invite by email" : "Assign by member email"}</label>
        <p className="text-xs text-[#8FA89B] mb-2">{inviteNew ? "We'll create their Location Manager account and email a set-password invite." : "Enhancers keep their role (dual). Plain members are upgraded to Location Manager."}</p>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="manager@email.com" data-testid="admin-manager-email" className="eyw-input w-full" />
        <div className="flex justify-between items-center mt-5">
          {location.manager_id ? <button onClick={unassign} className="text-xs text-red-500 hover:text-red-600">Remove current manager</button> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
            <button onClick={assign} disabled={saving} data-testid="admin-manager-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} {inviteNew ? "Invite & assign" : "Assign"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
